import {
  isPromotionBenefitDisplayable,
  type PromotionEvaluation,
  type PromotionFreeItem,
  type PromotionOffer,
} from './promotionEvaluation'

export type OfferBenefitKind = 'discount' | 'cashback' | 'free' | 'set_item' | 'other'

export interface OfferBenefitView {
  title?: string
  /** e.g. 'offers.gotDiscount' — UI translates; mapper never calls i18n. */
  titleKey?: string
  description?: string
  descriptionKey?: string
  /** Optional interpolation values for descriptionKey (e.g. percent). */
  descriptionParams?: Record<string, string>
  amount?: number
  showMinus: boolean
  kind: OfferBenefitKind
  products: PromotionFreeItem[]
}

export interface CheckoutPromotionViewData {
  offers: PromotionOffer[]
  freeItems: PromotionFreeItem[]
}

export const emptyCheckoutPromotionViewData: CheckoutPromotionViewData = {
  offers: [],
  freeItems: [],
}

/** Simplified view data from a single evaluation (no cart duplicate filtering). */
export function fromEvaluation(
  evaluation: PromotionEvaluation,
): CheckoutPromotionViewData {
  return {
    offers: evaluation.offers,
    freeItems: evaluation.freeItems,
  }
}

/** Discount, cashback, set-item — for receipt summary rows. */
export function offerSummaryRows(
  display: CheckoutPromotionViewData,
): OfferBenefitView[] {
  return offerBenefitRows(display).filter(
    (row) =>
      row.products.length === 0 &&
      row.amount != null &&
      row.amount > 0 &&
      row.kind !== 'free',
  )
}

export function receiptPromotionLabel(
  row: OfferBenefitView,
  t: (key: string) => string,
): string {
  switch (row.kind) {
    case 'cashback':
      return t('offers.benefitCashback')
    case 'set_item':
      return t('offers.benefitSetItemDiscount')
    case 'discount':
      return t('offers.benefitDiscount')
    default:
      if (row.showMinus) return t('offers.benefitDiscount')
      return row.title ?? t('offers.benefitDiscount')
  }
}

export const CheckoutPromotionViewData = {
  empty: emptyCheckoutPromotionViewData,
  fromEvaluation,
}

/**
 * Maps already-parsed checkout offer data into card rows.
 * Does not invent titles, amounts, or products.
 */
export function offerBenefitRows(
  display: CheckoutPromotionViewData,
): OfferBenefitView[] {
  const fromBenefits = rowsFromBenefitObjects(display)
  if (fromBenefits.length > 0) return fromBenefits
  return rowsFromOfferFields(display)
}

export function formatOfferAmount(amount: number): string {
  if (amount % 1 === 0) return amount.toFixed(0)
  return amount.toFixed(2)
}

function rowsFromBenefitObjects(
  display: CheckoutPromotionViewData,
): OfferBenefitView[] {
  const hasBenefits = display.offers.some((offer) => offer.benefits.length > 0)
  if (!hasBenefits) return []

  const rows: OfferBenefitView[] = []
  let insertedProducts = false

  for (const offer of display.offers) {
    for (const benefit of offer.benefits) {
      if (benefit.freeItems.length > 0) {
        if (!insertedProducts && display.freeItems.length > 0) {
          rows.push({
            ...titleFields(benefit.title, labelKeyForType(benefit.type)),
            description: benefit.description,
            showMinus: false,
            kind: 'free',
            products: display.freeItems,
          })
          insertedProducts = true
        }
        continue
      }
      if (!isPromotionBenefitDisplayable(benefit)) continue
      const kind = kindForType(benefit.type)
      rows.push({
        ...titleFields(benefit.title, labelKeyForType(benefit.type)),
        description: benefit.description,
        amount: benefit.amount,
        showMinus: isDiscountType(benefit.type),
        kind,
        products: [],
      })
    }
  }

  if (!insertedProducts && display.freeItems.length > 0) {
    rows.push({
      showMinus: false,
      kind: 'free',
      products: display.freeItems,
    })
  }

  if (rows.length === 0) return []
  return rows
}

function rowsFromOfferFields(
  display: CheckoutPromotionViewData,
): OfferBenefitView[] {
  const rows: OfferBenefitView[] = []
  let productTitle: string | undefined

  for (const offer of display.offers) {
    const descriptionFields = offerDescription(offer)
    const hasAmount = offer.discountAmount != null && offer.discountAmount > 0
    const hasSetItem =
      offer.setItemPriceSavings != null && offer.setItemPriceSavings > 0
    const hasText =
      (offer.title != null && offer.title.length > 0) ||
      (descriptionFields.description != null &&
        descriptionFields.description.length > 0) ||
      descriptionFields.descriptionKey != null
    const freeOnly =
      offer.freeItems.length > 0 && !hasAmount && !hasStandaloneCopy(offer)

    if (freeOnly) {
      productTitle ??= offer.title
      continue
    }

    // When an offer is only a set-item savings, its descriptive text belongs
    // to the set-item row instead of a generic discount row.
    const setItemOnly =
      hasSetItem &&
      !hasAmount &&
      offer.title == null &&
      offer.discountText == null &&
      offer.discountPercentage == null &&
      offer.condition == null

    if (!setItemOnly && (hasText || hasAmount)) {
      rows.push({
        ...titleFields(offer.title, 'offers.gotDiscount'),
        ...descriptionFields,
        amount: hasAmount ? offer.discountAmount : undefined,
        showMinus: hasAmount,
        kind: 'discount',
        products: [],
      })
    }

    if (hasSetItem) {
      rows.push({
        titleKey: 'offers.benefitSetItemDiscount',
        ...(setItemOnly ? descriptionFields : {}),
        amount: offer.setItemPriceSavings,
        showMinus: true,
        kind: 'set_item',
        products: [],
      })
    }

    if (offer.cashbackAmount != null && offer.cashbackAmount > 0) {
      rows.push({
        ...titleFields(offer.cashbackTitle, 'offers.gotCashback'),
        description: offer.cashbackText,
        amount: offer.cashbackAmount,
        showMinus: false,
        kind: 'cashback',
        products: [],
      })
    } else if (offer.cashbackText != null && offer.cashbackText.length > 0) {
      rows.push({
        ...titleFields(offer.cashbackTitle, 'offers.gotCashback'),
        description: offer.cashbackText,
        showMinus: false,
        kind: 'cashback',
        products: [],
      })
    }
  }

  if (display.freeItems.length > 0) {
    for (const item of display.freeItems) {
      rows.push({
        ...(item.name.length > 0
          ? { title: item.name }
          : titleFields(productTitle, 'offers.gotGift')),
        amount: item.originalPrice,
        showMinus: false,
        kind: 'free',
        products: [item],
      })
    }
  }

  return rows.filter(hasVisibleContent)
}

function hasVisibleContent(benefit: OfferBenefitView): boolean {
  return (
    (benefit.title != null && benefit.title.length > 0) ||
    benefit.titleKey != null ||
    (benefit.description != null && benefit.description.length > 0) ||
    benefit.descriptionKey != null ||
    (benefit.amount != null && benefit.amount > 0) ||
    benefit.products.length > 0
  )
}

function hasStandaloneCopy(offer: PromotionOffer): boolean {
  return (
    (offer.description != null && offer.description.length > 0) ||
    (offer.discountText != null && offer.discountText.length > 0) ||
    (offer.discountPercentage != null && offer.discountPercentage > 0) ||
    (offer.condition != null && offer.condition.length > 0)
  )
}

function offerDescription(offer: PromotionOffer): {
  description?: string
  descriptionKey?: string
  descriptionParams?: Record<string, string>
} {
  if (offer.description != null && offer.description.length > 0) {
    return { description: offer.description }
  }
  if (offer.discountText != null && offer.discountText.length > 0) {
    return { description: offer.discountText }
  }
  if (offer.discountPercentage != null && offer.discountPercentage > 0) {
    const percent =
      offer.discountPercentage % 1 === 0
        ? offer.discountPercentage.toFixed(0)
        : offer.discountPercentage.toFixed(2)
    return {
      descriptionKey: 'offers.discountPercentOfOrder',
      descriptionParams: { percent },
    }
  }
  if (offer.condition != null && offer.condition.length > 0) {
    return { description: offer.condition }
  }
  return {}
}

function isDiscountType(type: string | undefined): boolean {
  if (type == null || type.length === 0) return false
  const normalized = type.toLowerCase()
  return (
    normalized.includes('discount') ||
    normalized === 'percentage' ||
    normalized === 'percent'
  )
}

function labelKeyForType(type: string | undefined): string {
  const normalized = type?.toLowerCase() ?? ''
  if (normalized.includes('cash')) return 'offers.gotCashback'
  if (
    normalized.includes('free') ||
    normalized.includes('gift') ||
    normalized.includes('product')
  ) {
    return 'offers.gotGift'
  }
  return 'offers.gotDiscount'
}

function kindForType(type: string | undefined): OfferBenefitKind {
  const normalized = type?.toLowerCase() ?? ''
  if (normalized.includes('cash')) return 'cashback'
  if (
    normalized.includes('set_item') ||
    normalized.includes('setitem') ||
    normalized.includes('set-item')
  ) {
    return 'set_item'
  }
  if (
    normalized.includes('free') ||
    normalized.includes('gift') ||
    normalized.includes('product')
  ) {
    return 'free'
  }
  if (isDiscountType(type)) return 'discount'
  return 'other'
}

function titleFields(
  value: string | undefined,
  key: string,
): { title?: string; titleKey?: string } {
  if (value != null && value.length > 0) return { title: value }
  return { titleKey: key }
}
